import React, { useEffect, useState } from 'react';
import WordCard from './components/WordCard';
import Stats from './components/Stats';
import LoginPage from './components/LoginPage';
import UserProfile from './components/UserProfile';
import { useAuth } from './hooks/useAuth';
import { useWordManager } from './hooks/useWordManager';
import { useFavorites } from './hooks/useFavorites';
import './App.css';

function App() {
  const { user, userData, loading: authLoading, signInWithGoogle, signOutUser } = useAuth();
  const {
    words,
    currentWord,
    loading: wordsLoading,
    markAsLearned,
    markAsNotLearned,
    markAsSeen,
    getStats,
    currentDifficulty,
    nextWord,
  } = useWordManager(user?.uid || null);

  const [showStats, setShowStats] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const {
    favoriteIds,
    favoriteWords,
    isFavorite,
    toggleFavorite,
    getCurrent: getCurrentFavorite,
    nextFavorite,
    markFavoriteKnown,
    markFavoriteUnknown,
  } = useFavorites(user?.uid || null, words);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (currentWord?.id && !showFavorites) {
      markAsSeen(currentWord.id);
    }
  }, [currentWord?.id, showFavorites]);

  const [isProcessing, setIsProcessing] = useState(false);

  const handleSwipeLeft = () => {
    if (showFavorites) {
      const fav = getCurrentFavorite();
      if (fav) {
        markFavoriteUnknown(fav.id);
        nextFavorite();
      }
      return;
    }
    if (currentWord && !isProcessing) {
      setIsProcessing(true);
      const currentId = currentWord.id;
      markAsNotLearned(currentId);
      // Defer next to end of tick to avoid transient currentWord changes
      setTimeout(() => {
        nextWord();
        setIsProcessing(false);
      }, 200);
    }
  };

  const handleSwipeRight = () => {
    if (showFavorites) {
      const fav = getCurrentFavorite();
      if (fav) {
        markFavoriteKnown(fav.id);
        nextFavorite();
      }
      return;
    }
    if (currentWord && !isProcessing) {
      setIsProcessing(true);
      const currentId = currentWord.id;
      markAsLearned(currentId);
      // Defer next to end of tick to avoid transient currentWord changes
      setTimeout(() => {
        nextWord();
        setIsProcessing(false);
      }, 200);
    }
  };

  const handleSignIn = async () => {
    setSignInLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setSignInLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOutUser();
    setShowUserProfile(false);
  };

  // Giriş yapılmamışsa giriş sayfasını göster
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <LoginPage 
        onSignIn={handleSignIn} 
        loading={signInLoading} 
      />
    );
  }

  if (wordsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Kelimeler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Kelime Bulunamadı</h1>
          <p className="text-gray-600">Firebase'den kelime yüklenemedi.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          {/* Logo (click to show cards) */}
          <button
            type="button"
            onClick={() => { setShowStats(false); setMenuOpen(false); }}
            className="flex items-center focus:outline-none"
            aria-label="Anasayfa"
          >
            <img src="/logo.png" alt="İngilizce Kelime Kartlarım" className="h-10 md:h-12 object-contain" />
          </button>
          
          <div className="flex items-center space-x-2 relative">
            {/* Kullanıcı Avatar */}
            <button
              onClick={() => setShowUserProfile(true)}
              className="flex items-center space-x-2 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            >
              {userData?.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt={userData.displayName}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {userData?.displayName?.charAt(0) || userData?.email?.charAt(0)}
                  </span>
                </div>
              )}
              <span className="text-sm text-gray-700 hidden md:block">
                {userData?.displayName || 'Kullanıcı'}
              </span>
            </button>
            {/* Hamburger menu button next to name */}
            <button
              aria-label="Menü"
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none"
            >
              <svg className="w-6 h-6 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="6" width="18" height="2" rx="1" />
                <rect x="3" y="11" width="18" height="2" rx="1" />
                <rect x="3" y="16" width="18" height="2" rx="1" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
                  onClick={() => { setShowStats(true); setMenuOpen(false); }}
                >
                  İstatistikler
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-700"
                  onClick={() => { setShowFavorites(true); setShowStats(false); setMenuOpen(false); }}
                >
                  Favori Kelimeler
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-4">
        {showStats ? (
          <div>
            <div className="flex justify-between items-center mb-4">
              <button
                type="button"
                onClick={() => setShowStats(false)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                ← Geri
              </button>
            </div>
            <Stats stats={{ ...getStats(), currentDifficulty }} />
          </div>
        ) : showFavorites ? (
          <div>
            <div className="flex justify-between items-center mb-4">
              <button
                type="button"
                onClick={() => setShowFavorites(false)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                ← Geri
              </button>
              <div className="text-sm text-gray-600">{favoriteWords.length} favori</div>
            </div>
            <div className="relative flex flex-col items-center">
              {getCurrentFavorite() && (
                <WordCard
                  key={getCurrentFavorite()!.id}
                  word={getCurrentFavorite()!}
                  onSwipeLeft={handleSwipeLeft}
                  onSwipeRight={handleSwipeRight}
                  isProcessing={isProcessing}
                  onToggleFavorite={(id) => toggleFavorite(id)}
                  isFavorite={isFavorite(getCurrentFavorite()!.id)}
                />
              )}
              {favoriteWords.length === 0 && (
                <div className="text-center mt-6 text-gray-600">Henüz favori kelimen yok.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex flex-col items-center">
            {currentWord && (
              <WordCard
                key={currentWord.id}
                word={currentWord}
                onSwipeLeft={handleSwipeLeft}
                onSwipeRight={handleSwipeRight}
                isProcessing={isProcessing}
                onToggleFavorite={(id) => toggleFavorite(id)}
                isFavorite={isFavorite(currentWord.id)}
              />
            )}
            
            {/* Progress Indicator */}
            <div className="text-center mt-6">
              <p className="text-gray-600">Yeni kartlar yükleniyor...</p>
            </div>
          </div>
        )}
      </main>

      {/* User Profile Modal */}
      {showUserProfile && userData && (
        <UserProfile
          userData={userData}
          onSignOut={handleSignOut}
          onClose={() => setShowUserProfile(false)}
        />
      )}
    </div>
  );
}

export default App;
